/*
 * voice-labels/store.js — the label library in this browser (IndexedDB).
 *
 * Database `timbrica_voice_labels`, store `labels`, keyPath `id` — the same
 * names since 2026-03, so labels recorded before the redesign are still here.
 *
 * Row shapes:
 *   v2 (from 2026-10-22): { id, v:2, audio: ArrayBuffer|null, mime, text, lang,
 *       duration, expires, peaks:[0..255 ×48], createdAt, updatedAt }
 *   v1 (before): { id, audio: 'data:audio/webm;base64,…', description,
 *       duration, createdAt }
 * Audio is kept as ArrayBuffer, not Blob: a Blob in IndexedDB failed on older
 * Safari (WebKit stored it by reference to a file that was gone after a
 * restart); an ArrayBuffer is plain structured data on every engine.
 * `read()` hands every row back in the v2 shape; a v1 row is converted the
 * first time it is written again.
 */
(function () {
    'use strict';
    var DB_NAME = 'timbrica_voice_labels', DB_VERSION = 1, STORE = 'labels';
    var dbp = null;

    function open() {
        if (dbp) return dbp;
        dbp = new Promise(function (resolve, reject) {
            var req;
            try { req = indexedDB.open(DB_NAME, DB_VERSION); } catch (e) { reject(e); return; }
            req.onupgradeneeded = function () {
                var db = req.result;
                if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
            };
            req.onsuccess = function () {
                var db = req.result;
                /* Another tab upgrading the schema closes us; the next call reopens. */
                db.onversionchange = function () { try { db.close(); } catch (e) {} dbp = null; };
                resolve(db);
            };
            req.onerror = function () { dbp = null; reject(req.error); };
            req.onblocked = function () { /* another tab holds an old version; it resolves when that tab closes */ };
        });
        return dbp;
    }

    function tx(mode, fn) {
        return open().then(function (db) {
            return new Promise(function (resolve, reject) {
                var t = db.transaction(STORE, mode), out;
                var st = t.objectStore(STORE);
                out = fn(st);
                t.oncomplete = function () { resolve(out && out.result !== undefined ? out.result : out); };
                t.onerror = function () { reject(t.error); };
                t.onabort = function () { reject(t.error || new Error('aborted')); };
            });
        });
    }

    function dataUrlToBytes(s) {
        var m = /^data:([^;,]+)(;base64)?,(.*)$/i.exec(String(s || ''));
        if (!m) return null;
        var mime = m[1], body = m[3], bytes;
        if (m[2]) {
            var bin = atob(body);
            bytes = new Uint8Array(bin.length);
            for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        } else {
            bytes = new TextEncoder().encode(decodeURIComponent(body));
        }
        return { mime: mime, buf: bytes.buffer };
    }

    function bytesToDataUrl(buf, mime) {
        var b = new Uint8Array(buf), chunk = 0x8000, parts = [];
        for (var i = 0; i < b.length; i += chunk) parts.push(String.fromCharCode.apply(null, b.subarray(i, i + chunk)));
        return 'data:' + (mime || 'audio/mpeg') + ';base64,' + btoa(parts.join(''));
    }

    /* Any row → v2 shape (audio as ArrayBuffer). */
    function read(r) {
        if (!r) return null;
        var audio = null, mime = r.mime || '';
        if (r.audio instanceof ArrayBuffer) audio = r.audio;
        else if (r.audio && r.audio.buffer instanceof ArrayBuffer) audio = r.audio.buffer;
        else if (typeof r.audio === 'string' && r.audio) {
            var d = dataUrlToBytes(r.audio);
            if (d) { audio = d.buf; mime = d.mime; }
        }
        return {
            id: r.id,
            v: 2,
            audio: audio,
            mime: mime || (audio ? 'audio/webm' : ''),
            text: r.text != null ? String(r.text) : String(r.description || ''),
            lang: r.lang || '',
            duration: Number(r.duration) || 0,
            expires: r.expires || '',
            peaks: Array.isArray(r.peaks) ? r.peaks : null,
            createdAt: Number(r.createdAt) || 0,
            updatedAt: Number(r.updatedAt) || Number(r.createdAt) || 0,
            // Cloud (Premium shelf, cloud.js): when the voice last changed, which
            // cloud version this copy holds, and the updatedAt last pushed.
            audioAt: Number(r.audioAt) || 0,
            cloudVersion: Number(r.cloudVersion) || 0,
            cloudSyncedAt: Number(r.cloudSyncedAt) || 0,
        };
    }

    function all() {
        return tx('readonly', function (st) { return st.getAll(); }).then(function (rows) {
            return (rows || []).map(read).sort(function (a, b) { return (b.updatedAt || 0) - (a.updatedAt || 0); });
        });
    }
    function get(id) {
        return tx('readonly', function (st) { return st.get(id); }).then(read);
    }
    function put(row) {
        var r = read(row);
        return tx('readwrite', function (st) { st.put(r); }).then(function () { return r; });
    }
    function putMany(rows) {
        return tx('readwrite', function (st) { rows.forEach(function (r) { st.put(read(r)); }); });
    }
    function remove(id) {
        return tx('readwrite', function (st) { st.delete(id); });
    }

    /* For a backup: audio travels as a data URL, the rest as is. */
    function toBackupRow(r) {
        return {
            id: r.id, audio: r.audio ? bytesToDataUrl(r.audio, r.mime) : '',
            text: r.text || '', lang: r.lang || '', duration: r.duration || 0,
            expires: r.expires || '', peaks: r.peaks || null,
            createdAt: r.createdAt || 0, updatedAt: r.updatedAt || 0,
        };
    }
    function fromBackupRow(n) {
        var d = n.audio ? dataUrlToBytes(n.audio) : null;
        return read({
            id: n.id, audio: d ? d.buf : null, mime: d ? d.mime : '',
            text: n.text, lang: n.lang, duration: n.duration, expires: n.expires,
            peaks: Array.isArray(n.peaks) ? n.peaks : null,
            createdAt: n.createdAt, updatedAt: n.updatedAt,
        });
    }

    /* Ask the browser not to evict the library under storage pressure.
     * Resolves to true / false / null (API absent). */
    function persist() {
        try {
            if (!navigator.storage || !navigator.storage.persist) return Promise.resolve(null);
            return navigator.storage.persisted().then(function (p) { return p || navigator.storage.persist(); })
                .catch(function () { return null; });
        } catch (e) { return Promise.resolve(null); }
    }
    function persisted() {
        try {
            if (!navigator.storage || !navigator.storage.persisted) return Promise.resolve(null);
            return navigator.storage.persisted().catch(function () { return null; });
        } catch (e) { return Promise.resolve(null); }
    }

    window.VoiceLabelsStore = {
        all: all, get: get, put: put, putMany: putMany, remove: remove,
        toBackupRow: toBackupRow, fromBackupRow: fromBackupRow,
        persist: persist, persisted: persisted,
    };
})();
